/**
 * ActivityHost — full sub-activity 흡수.
 *
 * Layout 이 흡수된 sub-activity URL 에서는 RR Outlet swap 을 스킵하고 부모 main tab
 * 컴포넌트를 그대로 유지한다 (mainTabRegistry). 흡수된 sub-activity 콘텐츠는 여기서
 * fixed-position 오버레이로 별도 렌더한다. 결과적으로 sub-activity 진입/종료가 부모
 * 메인 페이지를 unmount 시키지 않는다.
 *
 * 추가:
 *   - auth: 로그인/회원가입/약관/본인인증 흐름.
 *   - SUB_ACTIVITY_RESOLVERS: 흡수된 모든 sub-activity kinds.
 *     같은 kind 안에서도 entry URL 에 따라 컴포넌트/AppBar 변형 가능.
 */

import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useActivityStore } from "../../store/activityStore";
import { ME_KEY } from "../../hooks/useMemberQueries";

// auth flow
import LoginPage from "../../pages/auth/LoginPage";
import ScanPage from "../../pages/scan/ScanPage";

// card / collection (스캔 결과 위 오버레이 + 홈/검색/세트에서 내려가는 상세)
import CardDetailPage from "../../pages/card/CardDetailPage";
import CardSharePage from "../../pages/card/CardSharePage";
import CardImagePage from "../../pages/card/CardImagePage";
import ScanSummaryPage from "../../pages/scan/ScanSummaryPage";
import SetDetailPage from "../../pages/card/SetDetailPage";
import VariantSelectPage from "../../pages/card/VariantSelectPage";
import HoldingFormPage from "../../pages/collection/HoldingFormPage";
import CollectionGroupDetailPage from "../../pages/collection/CollectionGroupDetailPage";
import CollectionGroupAddPage from "../../pages/collection/CollectionGroupAddPage";

// me flow
import ProfileEditPage from "../../pages/me/ProfileEditPage";
import WithdrawPage from "../../pages/me/WithdrawPage";

// support
import SupportPage from "../../pages/support/SupportPage";

// notifications
import NotificationDetailPage from "../../pages/notification/NotificationDetailPage";

// terms / privacy / marketing / oss viewer
import TermsViewerPage from "../../pages/auth/TermsViewerPage";
import { TERMS_TITLE_KEY, type TermsType } from "../terms/termsContent";

import RequireAuth from "./RequireAuth";
import AppBar from "./AppBar";
import { AppBarSlotOwner, useAppBarSlotValue } from "./AppBarSlotContext";
import { useBackDispatcher } from "../../navigation/dispatchBack";
import type { SubActivity, SubEntry } from "../../navigation/types";

const AUTH_ACTIVITY_KIND = "auth";
export const AUTH_ACTIVITY_FALLBACK_PATH = "/";

/**
 * 한 sub-activity entry 가 ActivityHost 안에서 어떻게 표시될지.
 * `appBarHidden: true` 면 풀스크린 (AppBar 없음).
 */
interface SubActivityResolved {
  component: ReactNode;
  /**
   * 앱바 제목. `nav.route.*` 로 시작하면 i18n 키로 보고 렌더 시 t() 로 푼다 (Layout 의
   * 라우트 handle 과 같은 규약 — 표시 언어를 바꿨는데 앱바만 한국어로 남는 걸 막는다).
   */
  appBarTitle?: string;
  appBarHidden?: boolean;
  /**
   * 'detail' (좌측 back chevron, default) | 'popup' (우측 X close)
   * | 'search' (좌측 back + 타이틀 + 우측 액션 아이콘).
   */
  appBarVariant?: "detail" | "popup" | "search";
  /** 우측 액션 아이콘 중 현재 페이지 강조 (회색 원형 배경). appBarVariant='search' 와 조합. */
  appBarActiveAction?: "search" | "notifications";
}

type SubActivityResolver = (entry: SubEntry) => SubActivityResolved | null;

function wrapAuth(node: ReactNode): ReactNode {
  return <RequireAuth>{node}</RequireAuth>;
}

/**
 * kind → entry URL 별로 적합한 컴포넌트 + AppBar 정책 반환.
 * App.tsx 의 라우트 element + handle 과 1:1 정합 — 변경 시 양쪽 동기화.
 */
const SUB_ACTIVITY_RESOLVERS: Record<string, SubActivityResolver> = {
  // 카메라 스캔 — 전체화면, 앱바 없음(화면이 자체 X 버튼을 그림 — 카메라 위 크롬)
  scan: () => ({
    component: <ScanPage />,
    appBarHidden: true,
  }),

  // 카드 상세 — 스캔 결과 "보기"(/scan/cards/:id)와 홈·검색·세트에서 내려가는
  // 일반 상세(/cards/:id) 를 같은 kind 로 받는다. ActivityHost 안에서는 라우트 매치가
  // 없어 useParams 가 비므로 식별자는 URL 에서 파싱해 prop 으로 준다.
  //
  // 앱바: 스캔 결과 위 오버레이는 흐름 종료성이 강해 X(popup), 일반 상세는 ←(detail).
  // 제목·공유 버튼은 페이지가 AppBarSlot 으로 올려보낸다 (카드명은 데이터에 달려 있다).
  "cards/detail": (entry) => {
    const m = /^(?:\/scan)?\/cards\/(\d+)$/.exec(entryPathname(entry.url));
    if (!m) return null;
    const fromScan = entryPathname(entry.url).startsWith("/scan/");
    return {
      component: <CardDetailPage cardSerialNo={Number(m[1])} />,
      appBarTitle: "nav.route.cardDetail",
      appBarVariant: fromScan ? "popup" : "detail",
    };
  },

  // 스캔 요약 (SC-011) — 스캔 activity 위 오버레이 → 닫기(X) 앱바.
  "scan/summary": () => ({
    component: <ScanSummaryPage />,
    appBarTitle: "nav.route.scanSummary",
    appBarVariant: "popup",
  }),

  // 카드 공유 (SC-020) — 카드 상세 위에 쌓인다 → 닫기(X) 앱바.
  "cards/share": (entry) => {
    const m = /^\/cards\/(\d+)\/share$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: <CardSharePage cardSerialNo={Number(m[1])} />,
      appBarTitle: "nav.route.share",
      appBarVariant: "popup",
    };
  },

  // 카드 원본 이미지 (SC-008) — 카드 상세 위에 쌓인다 → 닫기(X) 앱바.
  "cards/image": (entry) => {
    const m = /^\/cards\/(\d+)\/image$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: <CardImagePage cardSerialNo={Number(m[1])} />,
      appBarTitle: "nav.route.cardImage",
      appBarVariant: "popup",
    };
  },

  // 세부 버전 선택 (§3.3)
  "cards/variant": (entry) => {
    const m = /^\/cards\/([^/]+)\/variant$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: <VariantSelectPage cardSerialNo={m[1]} />,
      appBarTitle: "nav.route.variantSelect",
    };
  },

  // 세트 상세 (SC-006)
  "sets/detail": (entry) => {
    const m = /^\/sets\/([^/]+)$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: <SetDetailPage cardsetSerialNo={m[1]} />,
      appBarTitle: "nav.route.set",
    };
  },

  // 보유 저장 폼 — 스캔 결과·카드 상세의 "담기"(new) + 컬렉션 목록의 수정(:id).
  // §6.1 저장 폼은 위에 얹고 완료 시 닫아 아래 화면으로 복귀한다. cardSerialNo 는
  // 쿼리라 라우트 매치 없이도 useSearchParams 로 읽히지만, 수정 모드의
  // holdingSerialNo 는 path 라 URL 에서 파싱해 prop 으로 준다.
  "collections/holding": (entry) => {
    const m = /^\/(?:scan|collections)\/holdings\/([^/]+)$/.exec(entryPathname(entry.url));
    if (!m) return null;
    const isNew = m[1] === "new";
    return {
      component: wrapAuth(
        <HoldingFormPage holdingSerialNo={isNew ? undefined : m[1]} />,
      ),
      appBarTitle: isNew ? "nav.route.addHolding" : "nav.route.editHolding",
      appBarVariant: "popup",
    };
  },

  // 묶음 상세 (SC-014) / 수동 추가 (SC-016)
  "collections/group": (entry) => {
    const m = /^\/collections\/groups\/(\d+)$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: wrapAuth(<CollectionGroupDetailPage groupSerialNo={Number(m[1])} />),
      appBarTitle: "nav.route.collectionGroup",
    };
  },
  "collections/groupAdd": (entry) => {
    const m = /^\/collections\/groups\/(\d+)\/add$/.exec(entryPathname(entry.url));
    if (!m) return null;
    return {
      component: wrapAuth(<CollectionGroupAddPage groupSerialNo={Number(m[1])} />),
      appBarTitle: "nav.route.collectionGroupAdd",
      appBarVariant: "popup",
    };
  },

  "me/profile": () => ({
    component: wrapAuth(<ProfileEditPage />),
    appBarTitle: "nav.route.editProfile",
  }),
  // 설정 activity 는 2026-08-24 에 없앴다 (design) — 내용이 전부 내 정보(메인 탭)로 갔다.
  // `/me/settings` 주소는 App.tsx 가 `/me` 로 리다이렉트한다.
  // 아래는 그때 설정 위에 쌓이던 2단계 activity 였다 → 닫기(X) 앱바는 유지.
  "me/withdraw": () => ({
    component: wrapAuth(<WithdrawPage />),
    appBarTitle: "nav.route.withdraw",
    appBarVariant: "popup",
  }),

  // 알림 상세 — 좌측 타이틀 + 우측 X (popup).
  "notifications/detail": () => ({
    component: wrapAuth(<NotificationDetailPage />),
    appBarTitle: "nav.route.notificationDetail",
    appBarVariant: "popup",
  }),

  // 약관 / 개인정보 / 마케팅 / OSS 뷰어 — 정적 컨텐츠 SubActivity. type 파라미터로
  // AppBar 타이틀 결정.
  "terms/view": (entry) => {
    const search = entry.url.split("?")[1] ?? "";
    const params = new URLSearchParams(search);
    const raw = params.get("type");
    const type: TermsType =
      raw === "privacy" || raw === "marketing" || raw === "oss" ? raw : "service";
    return {
      component: <TermsViewerPage />,
      appBarTitle: TERMS_TITLE_KEY[type],
      appBarVariant: "popup",
    };
  },

  // 문의는 FAQ + 이메일 한 화면뿐이다 (SC-026 · 2026-08-25). 앱 안 1:1 문의
  // (작성·상세)는 함께 없어졌다 — 받는 서버가 없다.
  support: () => ({ component: <SupportPage />, appBarTitle: "nav.route.inquiry" }),
};

/**
 * Layout.tsx 가 동일 set 을 참조하므로 single source.
 * 새 sub-activity 추가 시 SUB_ACTIVITY_RESOLVERS 에만 등록하면 자동 흡수.
 */
export const ACTIVITY_HOST_HANDLED_KINDS = new Set(Object.keys(SUB_ACTIVITY_RESOLVERS));

/**
 * sub-activity 가 닫힐 때 invalidate 할 react-query keys.
 * 데이터를 mutate 하는 kind 만 등록 (읽기 전용은 등록 안 함).
 */
const SUB_ACTIVITY_INVALIDATE_ON_CLOSE: Record<string, ReadonlyArray<QueryKey>> = {
  // 프로필 수정 → 닉네임/프로필 갱신 (MyPage 의 Me 쿼리 refetch)
  "me/profile": [ME_KEY],
  // 문의 작성/수정 → 문의 리스트 갱신
  "support/inquiry": [],
};

/** sub-activity 닫힘 이벤트 — 비 react-query 페이지가 구독 가능. */
export const ACTIVITY_CLOSED_EVENT = "app:activity-closed";

export interface ActivityClosedDetail {
  kind: string;
  fallbackPath: string;
}

/**
 * Auth entry URL → component.
 */
function AuthEntryComponent({ entry: _entry }: { entry: SubEntry }) {
  // 가입 화면(약관·닉네임)을 없앤 뒤로 auth activity 의 엔트리는 로그인 하나뿐이다.
  return <LoginPage />;
}

/**
 * Auth activity chrome — sub entry 들은 자체 AppBar 의 back 으로 처리.
 */
function AuthActivityChrome({ entry, isTop }: { entry: SubEntry; isTop: boolean }) {
  const location = useLocation();

  if (isTop && location.pathname !== entryPathname(entry.url)) return null;

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflow: "auto" }}>
        <AuthEntryComponent entry={entry} />
      </div>
    </div>
  );
}

/** entry URL 의 pathname 만 추출 (search/hash 제외). */
function entryPathname(url: string): string {
  return url.split("?")[0].split("#")[0];
}

/** Sub-activity 한 개 (top entry) 의 chrome + content. */
function SubActivitySlot({ activity, isTop }: { activity: SubActivity; isTop: boolean }) {
  const dispatchBack = useBackDispatcher();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { t } = useTranslation("card");
  // 페이지가 올려보낸 제목·액션 (카드 상세의 카드명·공유). 자기 activity 소유일 때만.
  const slot = useAppBarSlotValue(activity.id);

  // Slot 이 unmount 되는 순간 = activity 가 close 된 순간. 이 때 아래에 있던 부모
  // 페이지 입장에서 데이터가 stale 했을 가능성이 있으므로 등록된 query key 들을
  // invalidate. 비 react-query 페이지를 위해 custom event 도 발사.
  useEffect(() => {
    return () => {
      const keys = SUB_ACTIVITY_INVALIDATE_ON_CLOSE[activity.kind];
      keys?.forEach((key) => {
        void queryClient.invalidateQueries({ queryKey: key });
      });
      const detail: ActivityClosedDetail = {
        kind: activity.kind,
        fallbackPath: activity.fallbackPath ?? "/",
      };
      window.dispatchEvent(new CustomEvent(ACTIVITY_CLOSED_EVENT, { detail }));
    };
  }, [activity.id, activity.kind, activity.fallbackPath, queryClient]);

  const entry = activity.entries[activity.entries.length - 1];

  const resolver = SUB_ACTIVITY_RESOLVERS[activity.kind];
  if (!resolver) return null;
  if (!entry) return null;
  // RR location 이 아직 entry URL 로 sync 안 됐으면 잠깐 빈 렌더 (top 슬롯만).
  if (isTop && location.pathname !== entryPathname(entry.url)) return null;
  const resolved = resolver(entry);
  if (!resolved) return null;
  // 라우트 제목 규약과 동일 — `nav.route.*` 는 i18n 키다 (Layout.tsx routeTitle 참조).
  const resolvedTitle = resolved.appBarTitle?.startsWith("nav.route.")
    ? t(resolved.appBarTitle)
    : resolved.appBarTitle;
  return (
    <AppBarSlotOwner id={activity.id}>
      {!resolved.appBarHidden && (
        <AppBar
          variant={resolved.appBarVariant ?? "detail"}
          title={slot.title ?? resolvedTitle}
          rightAction={slot.action}
          activeAction={resolved.appBarActiveAction}
          onBackPressed={() => dispatchBack("in-app")}
          onClosePressed={() => dispatchBack("in-app")}
        />
      )}
      <div style={{ flex: 1, overflow: "auto" }}>{resolved.component}</div>
    </AppBarSlotOwner>
  );
}

function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

export default function ActivityHost() {
  const activities = useActivityStore((s) => s.activities);

  const authActivity =
    [...activities].reverse().find((a) => a.kind === AUTH_ACTIVITY_KIND) ?? null;
  const authTopEntry =
    authActivity?.entries[authActivity.entries.length - 1] ?? null;
  // auth 의 stack 절대 위치 — 다른 SubActivity 와 z-index 비교 기준.
  const authStackIndex = authActivity ? activities.indexOf(authActivity) : -1;

  // 등록된 모든 sub-activity kinds 를 위→아래 stack 으로 렌더.
  const hostedSubActivities = activities.filter(
    (a) => a.kind !== AUTH_ACTIVITY_KIND && ACTIVITY_HOST_HANDLED_KINDS.has(a.kind),
  );

  useBodyScrollLock(authActivity !== null || hostedSubActivities.length > 0);

  return (
    <>
      <AnimatePresence>
        {hostedSubActivities.map((activity, idx) => {
          const absoluteIdx = activities.indexOf(activity);
          return (
            <motion.div
              key={activity.id}
              data-testid={`activity-host-${activity.kind.replace(/\//g, "-")}`}
              variants={subActivityVariants}
              initial="enter"
              animate={idx === hostedSubActivities.length - 1 ? "center" : "behind"}
              exit="exit"
              transition={subActivityTransition}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 900 + absoluteIdx,
                // scan: 네이티브 카메라 프리뷰가 웹뷰 **뒤** 레이어라 배경을 뚫어야 보인다
                // (html[data-scan-live] CSS 와 세트 — 인라인 style 은 CSS 로 못 덮으므로 여기서 분기)
                // ⚠ 배경 토큰은 --bg-page(테마 시맨틱) — 종전 --surface 는 존재하지 않는
                // 토큰이라 항상 #fff 폴백으로 떨어져 다크 테마에서도 흰 배경이 나왔다.
                background: activity.kind === "scan" ? "transparent" : "var(--bg-page)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                willChange: "transform, opacity",
              }}
            >
              <SubActivitySlot
                activity={activity}
                isTop={idx === hostedSubActivities.length - 1}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>

      <AnimatePresence>
        {authActivity && authTopEntry && (
          <motion.div
            key={authActivity.id}
            data-testid="activity-host-auth"
            variants={subActivityVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={subActivityTransition}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 900 + authStackIndex,
              background: "var(--bg-page)", // auth 오버레이도 테마 페이지 배경 (--surface 는 미존재 토큰이었다)
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              willChange: "transform, opacity",
            }}
          >
            <AuthActivityChrome
              entry={authTopEntry}
              isTop={authStackIndex === activities.length - 1}
            />
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}

// SubActivity open/close motion. iOS 카드 스택 — 280ms = Layout.tsx OVERLAY_EXIT_MS 정합.
const subActivityVariants = {
  enter: { x: "8%", opacity: 0 },     // 새 활동이 우측 바깥에서 진입
  center: { x: "0%", opacity: 1 },    // 활성 (top)
  behind: { x: "-8%", opacity: 0 },   // 위에 덮인 상태 — 좌측 + 페이드
  exit: { x: "8%", opacity: 0 },      // 닫힘 — 우측으로 나가며 페이드
};

const subActivityTransition = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1] as const,
};
